import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
	Badge,
	Box,
	Button,
	Group,
	Loader,
	Paper,
	SimpleGrid,
	Stack,
	Table,
	Text,
	Title,
} from '@mantine/core'
import { getExperienceSales } from '../services/actions/experienceActions'

const formatMoney = (amountCents) => {
	const cents = Number(amountCents || 0)
	if (!Number.isFinite(cents)) return '$0.00'
	return `$${(cents / 100).toFixed(2)}`
}

const formatDate = (value) => {
	if (!value) return 'Unknown'
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return 'Unknown'
	return date.toLocaleString()
}

const DetailRow = ({ label, value }) => (
	<tr>
		<td style={{ width: 180 }}>
			<Text size='sm' color='dimmed'>
				{label}
			</Text>
		</td>
		<td>{value || 'Not available'}</td>
	</tr>
)

const PurchaseDetailPage = () => {
	const { orgSlug, experienceSlug, saleId } = useParams()
	const navigate = useNavigate()
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')

	useEffect(() => {
		let isCurrent = true
		setLoading(true)
		setError('')

		getExperienceSales(experienceSlug, orgSlug, {
			saleId: decodeURIComponent(saleId || ''),
			pageSize: 1,
		})
			.then((result) => {
				if (!isCurrent) return
				setData(result)
			})
			.catch((err) => {
				if (!isCurrent) return
				console.error('Failed to load purchase detail:', err)
				setError('Failed to load purchase detail.')
			})
			.finally(() => {
				if (isCurrent) setLoading(false)
			})

		return () => {
			isCurrent = false
		}
	}, [experienceSlug, orgSlug, saleId])

	const sale = useMemo(() => {
		const decodedSaleId = decodeURIComponent(saleId || '')
		return (data?.sales || []).find((item) => item.saleId === decodedSaleId)
	}, [data, saleId])

	return (
		<Box p='md'>
			<Group position='apart' mb='md'>
				<Box>
					<Title order={2}>Purchase Detail</Title>
					<Text size='sm' color='dimmed'>
						Item sale, payment, seller, and traveler details.
					</Text>
				</Box>
				<Button
					variant='outline'
					onClick={() =>
						navigate(`/${orgSlug}/experiences/${experienceSlug}/overview/sales`)
					}
				>
					Sales Overview
				</Button>
			</Group>

			{loading ? (
				<Group position='center' py='xl'>
					<Loader />
				</Group>
			) : error ? (
				<Text color='red'>{error}</Text>
			) : !sale ? (
				<Text color='dimmed'>Purchase not found.</Text>
			) : (
				<Stack spacing='md'>
					<Paper withBorder p='md' shadow='xs'>
						<Group position='apart' align='flex-start'>
							<Box>
								<Title order={3}>{sale.itemName}</Title>
								<Text color='dimmed'>
									{sale.description || 'No description'}
								</Text>
							</Box>
							<Stack spacing={4} align='flex-end'>
								<Title order={3}>
									{formatMoney(
										sale.totalAmountCents ?? sale.paymentAmountCents,
									)}
								</Title>
								<Badge color={sale.issued ? 'teal' : 'gray'}>
									{sale.issued ? 'Issued' : 'Pending issue'}
								</Badge>
							</Stack>
						</Group>
					</Paper>

					<Paper withBorder p='md' shadow='xs'>
						<Title order={4} mb='sm'>
							Payment Breakdown
						</Title>
						<Table>
							<tbody>
								<DetailRow
									label='Item Price'
									value={formatMoney(
										sale.subtotalAmountCents ?? sale.paymentAmountCents,
									)}
								/>
								<DetailRow
									label='Checkout Fee'
									value={formatMoney(sale.customerFeeAmountCents)}
								/>
								<DetailRow
									label='Total Charged'
									value={formatMoney(
										sale.totalAmountCents ?? sale.paymentAmountCents,
									)}
								/>
							</tbody>
						</Table>
					</Paper>

					<SimpleGrid cols={2} breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
						<Paper withBorder p='md' shadow='xs'>
							<Title order={4} mb='sm'>
								Sale
							</Title>
							<Table>
								<tbody>
									<DetailRow label='Sale ID' value={sale.saleId} />
									<DetailRow label='Sold At' value={formatDate(sale.soldAt)} />
									<DetailRow
										label='Payment Method'
										value={sale.paymentMethod}
									/>
									<DetailRow
										label='Payment Status'
										value={sale.paymentStatus}
									/>
									<DetailRow label='Staff' value={sale.sellerName} />
									<DetailRow label='Staff Email' value={sale.sellerEmail} />
									<DetailRow label='Source' value={sale.source} />
								</tbody>
							</Table>
						</Paper>

						<Paper withBorder p='md' shadow='xs'>
							<Title order={4} mb='sm'>
								Traveler
							</Title>
							<Table>
								<tbody>
									<DetailRow label='Name' value={sale.travelerName} />
									<DetailRow label='Email' value={sale.travelerEmail} />
									<DetailRow label='Phone' value={sale.travelerPhone} />
									<DetailRow label='Account' value={sale.accountName} />
									<DetailRow
										label='Booking Number'
										value={sale.bookingNumber}
									/>
									<DetailRow label='Station' value={sale.stationName} />
								</tbody>
							</Table>
						</Paper>
					</SimpleGrid>

					<Paper withBorder p='md' shadow='xs'>
						<Title order={4} mb='sm'>
							System References
						</Title>
						<Table>
							<tbody>
								<DetailRow label='Check-In ID' value={sale.checkInId} />
								<DetailRow label='Booking Item ID' value={sale.bookingItemId} />
								<DetailRow label='Booking ID' value={sale.bookingId} />
								<DetailRow label='Order ID' value={sale.orderId} />
								<DetailRow label='Transaction ID' value={sale.transactionId} />
							</tbody>
						</Table>
						<Group mt='md'>
							<Button
								onClick={() =>
									navigate(
										`/${orgSlug}/experiences/${experienceSlug}/checkIn/${sale.checkInId}`,
									)
								}
							>
								Open Check-In
							</Button>
						</Group>
					</Paper>
				</Stack>
			)}
		</Box>
	)
}

export default PurchaseDetailPage
